import React, { ReactNode } from 'react';
import styled from 'styled-components/macro';
import { labelStyles } from '../../../../../shared/form/FieldLabel';
import {
  colourNeutral09,
  colourNeutral10,
  colourPurple05,
  colourPurple10,
  colourRed05,
  colourRed10,
  colourYellow05,
  colourYellow10,
} from '../../../../../styling/colours';
import { fontSize01 } from '../../../../../styling/fonts';
import { spacing01, spacing02, spacing04 } from '../../../../../styling/spacing';
import { NODE_WIDTH } from '../map-helpers/mapHelpers';
import { NodeState } from '../treeNode';
import { SourceHandle, TargetHandle } from './Handle';

type Props = {
  icon: ReactNode;
  stats: Array<string>;
  children?: ReactNode;
  hasSource: boolean;
  hasTarget: boolean;
  title: string;
  state?: NodeState;
  markedForDeletion: boolean;
  popoverButtons?: JSX.Element;
  className?: string;
};
export const MapFlowNode = ({
  icon,
  stats,
  children,
  hasSource,
  hasTarget,
  title,
  state,
  markedForDeletion,
  popoverButtons,
  className,
}: Props) => {
  return (
    <FlowNode $state={state} $markedForDeletion={markedForDeletion} className={className}>
      <div>
        <FlowNodeTitle>
          {icon} {title}
        </FlowNodeTitle>
        {children}
        <Statistics>{stats.map((stat, index) => stat !== '' && <Statistic key={index}>{stat}</Statistic>)}</Statistics>
      </div>
      {hasTarget && <TargetHandle />}
      {hasSource && <SourceHandle />}
      {popoverButtons && <PopoverButtons>{popoverButtons}</PopoverButtons>}
    </FlowNode>
  );
};

export const FlowNode = styled.div<{ $markedForDeletion: boolean; $state: NodeState | undefined }>`
  width: ${NODE_WIDTH}px;
  border: 2px solid ${props => getColour(props.$state, props.$markedForDeletion, 'Border')};
  padding: ${spacing02};
  background: ${props => getColour(props.$state, props.$markedForDeletion, 'Background')};
`;

const getColour = (state: NodeState | undefined, markedForDeletion: boolean, area: 'Border' | 'Background') => {
  if (markedForDeletion) {
    return area === 'Border' ? colourPurple05 : colourPurple10;
  }

  if (state === 'Warning') {
    return area === 'Border' ? colourYellow05 : colourYellow10;
  }

  if (state === 'Error') {
    return area === 'Border' ? colourRed05 : colourRed10;
  }

  return area === 'Border' ? colourNeutral09 : colourNeutral10;
};

const FlowNodeTitle = styled.title`
  display: flex;
  align-items: center;
  svg {
    margin-right: ${spacing01};
  }
  margin-bottom: ${spacing04};
  ${labelStyles}
`;

const Statistics = styled.ul`
  font-size: ${fontSize01};
  list-style-type: none;
  padding: 0;
`;

const Statistic = styled.li`
  text-align: right;
`;

const PopoverButtons = styled.div`
  position: absolute;
  top: ${spacing01};
  right: 6px;
  display: flex;
`;
